import React from 'react';
import { Link } from 'react-router-dom';
import RichText from './RichText';
import ReportShell, { useReportContext, useScrollToHash } from './ReportShell';
import { useSittingReport } from '../sittingReport';
import { openingAddsNothing } from '../promptText';

/**
 * Review: the model answers, for a written paper or a live interview just
 * finished. For each question, what it tests, a model answer, and each
 * follow-up with the answer it was looking for. All authored content, read
 * back as written. The student's own answers and marks are on Feedback,
 * linked question by question.
 */
export default function InterviewReviewPage() {
  const ctx = useReportContext();
  const { status, sitting } = useSittingReport(ctx.runIds, { mode: ctx.mode });
  useScrollToHash(status === 'ready');

  return (
    <ReportShell active="review" title="Review" status={status}>
      <p className="ib__lede">
        For each question: what it tests, a model answer, and what each follow-up was looking for.
      </p>
      {sitting.map((q, i) => {
        const followUps = (q.rungs || []).filter((r) => !r.opening_only);
        return (
          <section className="ir-q" id={`q-${i + 1}`} key={q.run_id}>
            <h2 className="ir-q-title">Question {i + 1}</h2>
            {!openingAddsNothing(q.opening_prompt, followUps[0]?.prompt) && <RichText text={q.opening_prompt} />}

            {q.target_insight && (
              <>
                <div className="ir-label">What this question tests</div>
                <RichText text={q.target_insight} />
              </>
            )}

            {q.model_answer ? (
              <>
                <div className="ir-label">Model answer</div>
                <div className="ir-box"><RichText text={q.model_answer} /></div>
              </>
            ) : q.strong_answer && (
              <>
                <div className="ir-label">What a strong answer does</div>
                <div className="ir-box"><RichText text={q.strong_answer} /></div>
              </>
            )}

            {q.model_answer && q.weaker_answer && (
              <>
                <div className="ir-label">A weaker answer</div>
                <div className="ir-box ir-box--weaker"><RichText text={q.weaker_answer} /></div>
                {q.why_weaker && (
                  <>
                    <div className="ir-label">Why it is weaker</div>
                    <RichText text={q.why_weaker} />
                  </>
                )}
              </>
            )}

            {followUps.length > 0 && (
              <>
                <div className="ir-label">Follow-up questions</div>
                {followUps.map((r) => (
                  <div className="ir-part" key={r.rung_id}>
                    <RichText text={r.prompt} className="ir-part-prompt" />
                    {(r.expected || r.strong_text) && (
                      <div className="ir-answer">
                        <div className="ir-answer-label">{r.expected ? 'Answer' : 'A strong answer'}</div>
                        <RichText text={r.expected || r.strong_text} />
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}

            <Link
              className="ir-crosslink"
              to={`${ctx.base}/feedback#q-${i + 1}`}
              state={ctx.linkState}
            >
              Your answers and marks for this question →
            </Link>
          </section>
        );
      })}
    </ReportShell>
  );
}
